/**
 * Pure-data English catalogue for namespace 'pgr' (W1-08 formats-legacy-respack; imports only core/util/types):
 * import report entries of `importPgr` (official Phigros JSON). Params are named in braces; `list` is the JSON
 * key of an event list and is translated with ICU `select`. The import report shows an entry's `count` and
 * `path` next to the text.
 */

import type { MessageCatalog } from '../../core/util/types';

const LIST =
  '{list, select,' +
  ' judgeLineDisappearEvents {opacity (judgeLineDisappearEvents)}' +
  ' judgeLineRotateEvents {rotation (judgeLineRotateEvents)}' +
  ' judgeLineMoveEvents {position (judgeLineMoveEvents)}' +
  ' speedEvents {speed (speedEvents)}' +
  ' other {{list}}}';

const catalog: MessageCatalog = {
  ns: 'pgr',
  locale: 'en',
  messages: {
    // ---- Problems Phira refuses or crashes on (repaired) ----
    'report.notObject':
      'The file is not a Phigros chart: its content is not a JSON object. An empty chart was created.',
    'report.missingField':
      'The required field "{field}" is missing. A default was used; Phira refuses charts with missing fields.',
    'report.wrongType': 'The field "{field}" has the wrong kind of value and was ignored; Phira refuses such charts.',
    'report.unknownNoteType':
      'A note has type {type}, which is not one of 1 (Tap), 2 (Drag), 3 (Hold) or 4 (Flick). It was left out; Phira refuses such charts.',
    'report.formatVersion':
      'formatVersion {version} is not supported by Phira (only 1 and 3 are). The chart was read as formatVersion 3.',
    'report.invalidBpm':
      'A judge line has the BPM {bpm}, which is not a positive number. The chart tempo was used for it; Phira would show garbage timing.',
    'report.emptyList': `The ${LIST} list of this line is empty. Phira would crash on it; the property was left unset.`,

    // ---- Changed (rendering may differ slightly) ----
    'report.unsortedEvents': `The ${LIST} events are not in time order. They were converted the way Phira plays them forward; seeking backwards in Phira can show something else.`,
    'report.nanLeadIn': `The first ${LIST} event has no length, so Phira shows an invalid value before it. The first value is used there instead.`,
    'report.speedJump':
      'Overlapping speed events make the note distance jump in Phira. The jump cannot be expressed with speed and was smoothed out.',
    'report.speedAfterMaxTime':
      'A speed event starts more than one second after the last note. Phira keeps the line moving until that event starts and then makes the note distance jump; the import keeps the movement until then and stops it there instead of jumping.',
    'report.negativeHold': 'A hold note has a negative length. It was made zero-length.',
    'report.inexactTimes': 'Some times could not be expressed exactly in beats and were rounded (at most {maxMs} ms).',
    'report.timeOutOfRange':
      'Some times are so large or so far before the start that a chart cannot hold them. They were moved to the nearest time a chart can hold.',

    // ---- Normalised (structure changed, Phira plays it the same) ----
    'report.multiBpm':
      'The judge lines use {count} different BPMs. The chart tempo is {bpm} BPM and the other lines were converted through seconds, so they play at the same times.',
    'report.reversedEvents': `Events in the ${LIST} list that end before they start were left out, as Phira ignores them.`,
    'report.lastMotionIgnored': `Phira ignores the movement of the last ${LIST} event and keeps its start value. The import does the same.`,
    'report.speedGaps':
      'Speed events have gaps or overlaps. Phira uses each event for its own length, and the import reproduces the resulting speed.',
    'report.thickness':
      'Judge lines got a thickness of 1.0002 because Phira draws Phigros lines thinner than RPE lines.',

    // ---- Flagged (kept exactly) ----
    'report.holdTailFrozen':
      'A hold ends more than one second after the last note starts. Phira stops the line movement there, so its end is drawn shorter; the import keeps that behaviour.',
  },
};

export default catalog;
